import React, { FunctionComponent, useEffect } from 'react'

import {
  Route,
  Routes as Switch,
  Navigate as Redirect,
  useMatch as useRouteMatch,
} from 'react-router-dom'
import { useLocation } from 'react-use'

import { config } from '@fairhq/common'
import { Admin } from 'features/admin/Admin'
import { Assessment } from 'features/assessment/Assessment'
import { AssessmentQuestions } from 'features/assessmentQuestions/AssessmentQuestions'
import { AssessmentChanges } from 'features/changelog/Changelog'
import { Home } from 'features/home/Home'
import { Maintenance } from 'features/maintenance/Maintenance'
import { NotFound } from 'features/notFound/NotFound'
import { ReportsDiversity } from 'features/reportsDiversity/ReportsDiversity'
import { ReportsEquality } from 'features/reportsEquality/ReportsEquality'
import { ReportsEqualityAreas } from 'features/reportsEqualityAreas/ReportsEqualityAreas'
import { ReportsExperience } from 'features/reportsExperience/ReportsExperience'
import { ReportExperienceCategories } from 'features/reportsExperienceCategories/ReportsExperienceCategories'
import { Resources } from 'features/resources/Resources'
import { Strategy } from 'features/strategy/Strategy'
import { StrategyRecommendation } from 'features/strategyRecommendation/StrategyRecommendation'
import { StrategyTask } from 'features/strategyTask/StrategyTask'
import { Surveys } from 'features/surveys/Surveys'
import { useAdminRedirect } from 'hooks/useAdminRedirect'
import { useAlert } from 'hooks/useAlert'
import { PermissionsEnum, useAuthPermissions } from 'hooks/useAuthPermissions'
import { useAuthRoles } from 'hooks/useAuthRoles'
import { EntitlementsGuard } from 'routes/EntitlementsGuard'
import { EntitlementsEnum } from 'store/company/types'
import { UIComponentsExamples } from 'ui-kit/examples/UIComponentsExamples'

import { LocalAreas } from '../features/assessment/LocalAreas'

import { PrivateRoute } from './PrivateRoute'
import { useCanOverrideMaintenance } from './useCanOverrideMaintenance'

const getShouldRedirect = (
  isUnderMaintenance: boolean,
  canOverrideMaintenance: boolean,
  isSecretLogin: boolean
): boolean => {
  if (isUnderMaintenance) {
    if (canOverrideMaintenance) {
      return false
    }
    return !isSecretLogin
  }
  return false
}

const RoutesComponent: FunctionComponent = () => {
  const { permissions } = useAuthPermissions()
  const { isAdmin } = useAuthRoles()

  useAdminRedirect()
  useAlert()
  const location = useLocation()

  useEffect(() => {
    window?.gtag?.('config', config.analytics, { page_path: location.pathname })
  }, [location.pathname])

  const isUnderMaintenance = config.maintenanceMode === 'true'
  const canOverrideMaintenance = useCanOverrideMaintenance()
  const isSecretLogin = !!useRouteMatch('/bypass-maintenance')
  const maintenanceRedirect = getShouldRedirect(
    isUnderMaintenance,
    canOverrideMaintenance,
    isSecretLogin
  )

  return (
    <Switch>
      <Route key="/maintenance" path="/maintenance" element={<Maintenance />} />
      {maintenanceRedirect && (
        <Route path="/" element={<Redirect to="/maintenance" />} />
      )}
      <Route
        path="/bypass-maintenance"
        element={
          <PrivateRoute key="/bypass-maintenance">
            <Redirect to="/assessment" />
          </PrivateRoute>
        }
      />
      {isAdmin && (
        <Route
          path="/admin"
          element={
            <PrivateRoute key="/admin">
              <Admin />
            </PrivateRoute>
          }
        />
      )}

      <Route
        path="/ui"
        element={
          <PrivateRoute>
            <UIComponentsExamples />
          </PrivateRoute>
        }
      />

      <Route
        path="/"
        element={
          <PrivateRoute>
            <Home />
          </PrivateRoute>
        }
      />
      {permissions?.includes(PermissionsEnum.READ_REPORTS) && (
        <>
          <Route
            path="/equality"
            element={
              <PrivateRoute key="/equality">
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.ADVANCED_ASSESSMENT_AUDIT]}
                  component={<ReportsEquality />}
                />
              </PrivateRoute>
            }
          />

          <Route
            path="/equality/:code?"
            element={
              <PrivateRoute key="/equality/:code">
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.ADVANCED_ASSESSMENT_AUDIT]}
                  component={<ReportsEqualityAreas />}
                />
              </PrivateRoute>
            }
          />

          <Route
            path="/diversity"
            element={
              <PrivateRoute key="/diversity">
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.DEM]}
                  component={<ReportsDiversity />}
                />
              </PrivateRoute>
            }
          />

          <Route
            path="/diversity/:code?"
            element={
              <PrivateRoute key="/diversity/:code">
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.DEM]}
                  component={<ReportsDiversity />}
                />
              </PrivateRoute>
            }
          />

          <Route
            path="/experience"
            element={
              <PrivateRoute key="/experience">
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.IS, EntitlementsEnum.PP]}
                  component={<ReportsExperience />}
                />
              </PrivateRoute>
            }
          />

          <Route
            path="/experience/:category"
            element={
              <PrivateRoute key="/experience/:category">
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.IS, EntitlementsEnum.PP]}
                  component={<ReportExperienceCategories />}
                />
              </PrivateRoute>
            }
          />

          <Route
            path="/strategy"
            element={
              <PrivateRoute>
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.STRATEGY]}
                  component={<Strategy />}
                />
              </PrivateRoute>
            }
          />

          <Route
            path="/strategy/recommendation/:code"
            element={
              <PrivateRoute>
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.STRATEGY]}
                  component={<StrategyRecommendation />}
                />
              </PrivateRoute>
            }
          />

          <Route
            path="/strategy/task/:code"
            element={
              <PrivateRoute>
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.STRATEGY]}
                  component={<StrategyTask />}
                />
              </PrivateRoute>
            }
          />

          <Route
            path="/resources/:code"
            element={
              <PrivateRoute>
                <Resources />
              </PrivateRoute>
            }
          />
        </>
      )}

      {permissions?.includes(PermissionsEnum.READ_ASSESSMENT) && (
        <>
          <Route
            path="/assessment"
            element={
              <PrivateRoute key="/assessment">
                <Assessment />
              </PrivateRoute>
            }
          />
          <Route
            path="/assessment/:code"
            element={
              <PrivateRoute key="/assessment/:code">
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.ADVANCED_ASSESSMENT_AUDIT]}
                  component={<LocalAreas />}
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/assessment/:code/questions"
            element={
              <PrivateRoute>
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.ADVANCED_ASSESSMENT_AUDIT]}
                  component={<AssessmentQuestions />}
                />
              </PrivateRoute>
            }
          />
          <Route
            path="/assessment/changelog"
            element={
              <PrivateRoute key="/assessment/changelog">
                <EntitlementsGuard
                  entitlements={[EntitlementsEnum.ADVANCED_ASSESSMENT_AUDIT]}
                  component={<AssessmentChanges />}
                />
              </PrivateRoute>
            }
          />
        </>
      )}

      {permissions?.includes(PermissionsEnum.READ_SURVEYWINDOW) && (
        <Route
          path="/survey"
          element={
            <PrivateRoute>
              <EntitlementsGuard
                entitlements={[EntitlementsEnum.SURVEYS]}
                component={<Surveys />}
              />
            </PrivateRoute>
          }
        />
      )}

      <Route path="*" element={<NotFound />} />
    </Switch>
  )
}

export const Routes = React.memo(RoutesComponent)
